import { useState, useEffect } from "react"
import { ClipboardIcon } from "lucide-react"
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'

import oneDark from "react-syntax-highlighter/dist/cjs/styles/prism/one-dark"
import { Button } from "./ui/button"

interface CodeBlockProps {
  code: string
  language?: string
  header?: string
  bgColor?: string
  clipboardColor?: string
}

export function CodeBlock({ code, language = "json", header, bgColor, clipboardColor }: CodeBlockProps) {
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const style =
    language === "none"
      ? {
          'pre[class*="language-"]': {
            background: "transparent",
            color: "inherit",
            textShadow: "none",
          },
          'code[class*="language-"]': {
            background: "transparent",
            color: "inherit",
            textShadow: "none",
          },
        }
      : {
          ...oneDark,
          'pre[class*="language-"]': {
            ...oneDark['pre[class*="language-"]'],
            background: "transparent",
            textShadow: "none",
          },
          'code[class*="language-"]': {
            ...oneDark['code[class*="language-"]'],
            background: "transparent",
            textShadow: "none",
          },
        }

  return (
    <div className={`relative p-2 group rounded-md ${bgColor || "bg-muted"}`}>
      {header && (
        <div className="text-sm text-muted-foreground mb-2">{header}</div>
      )}
      <Button
        variant="ghost"
        size="sm"
        shape="rectangle"
        onClick={() => {
          navigator.clipboard.writeText(code)
        }}
        className={`absolute right-2 opacity-0 group-hover:opacity-100 transition-opacity ${clipboardColor || "text-muted-foreground"}`}
      >
        <ClipboardIcon className="h-4 w-4" />
      </Button>
      <div className="[&>pre]:!bg-transparent [&>pre]:!p-0 min-h-[2rem] flex items-center">
        {isMounted ? (
          <SyntaxHighlighter
            language={language}
            style={style}
            customStyle={{
              background: "transparent",
              margin: 0,
              fontSize: "0.875rem",
              textShadow: "none",
              textAlign: "left",
              color: language === "none" ? "inherit" : undefined,
            }}
            wrapLongLines={false}
          >
            {code}
          </SyntaxHighlighter>
        ) : (
          <pre className="text-sm whitespace-pre-wrap font-mono">{code}</pre>
        )}
      </div>
    </div>
  )
}
